import DeviceLogsPanel from "@/components/admin/DeviceLogsPanel";
import TelegramHub from "@/components/admin/TelegramHub";
import RemoteAccessPanel from "@/components/admin/RemoteAccessPanel";
import AiAssistantPanel from "@/components/admin/AiAssistantPanel";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Activity, ChevronRight, HardDrive, Lock, Printer, Search, ServerCrash, Wifi, X } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import ReportsPanel from "@/pages/ReportsPanelV2";
import CustomReportBuilder from "@/components/admin/CustomReportBuilder";
import PrinterRegistry from "@/components/PrinterRegistry";
import SettingsPortability from "@/components/SettingsPortability";
import BackupsPanel from "@/components/admin/BackupsPanel";
import OrderListsPanel from "@/components/admin/OrderListsPanel";
import PrintJobsPanel from "@/components/admin/PrintJobsPanel";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAdminData } from "@/hooks/useAdminData";
import useSettingsDrafts, { validateDraftBatch } from "@/hooks/useSettingsDrafts";
import StaffPanel from "@/components/admin/StaffPanel";
import RolesPanel from "@/components/admin/RolesPanel";
import MenuHub from "@/components/admin/menu/MenuHub";
import FloorHub from "@/components/admin/FloorHub";
import PrintRoutingPanel from "@/components/admin/PrintRoutingPanel";
import AdminNavTree from "@/components/admin/AdminNavTree";
import SettingBand from "@/components/admin/SettingBand";
import SettingsSaveBar from "@/components/admin/SettingsSaveBar";
import SettingsAuditPanel from "@/components/admin/SettingsAuditPanel";
import { OUTLET_SCOPE } from "@/components/admin/adminScope";
import {
  buildConfigTree, categoryNodeId, defaultSelection, domainNodeId, firstCategory, locateSetting, pathToCategory, slug,
} from "@/components/admin/adminTree";
import { searchAdmin, ADMIN_MODULES } from "@/lib/settingsMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const describeError = error =>
  error?.response?.data?.detail || error?.message || "Request failed";

/* ── AdminPage ──────────────────────────────────────────────────── */

/**
 * A dense, layered configuration console (§58), not a giant form.
 *
 *   ┌──────────────┬──────────────────────────────────────────────────┐
 *   │ nav tree     │ Administration › Billing › Charges   Editing: ▾  │
 *   │ Domain       │ Applies to: This Outlet                          │
 *   │  Category    ├──────────────────────────────────────────────────┤
 *   │   Setting    │ compact rows, advanced + expert behind expanders │
 *   └──────────────┴──────────────────────────────────────────────────┘
 *
 * Three things this page deliberately does NOT have any more:
 *   · a Simple | Advanced | Expert selector — the tiers are disclosure, not a
 *     difficulty setting the owner is asked to pick (§20),
 *   · a scope selector of ANY kind — the tree edits the outlet layer, and
 *     device behavior is edited on the device's own surface (§18, rewritten
 *     2026-08-20; the old "Editing: This Outlet ▾" header control is gone),
 *   · a flat page of every setting in a domain — the tree is the IA (§19).
 */
/**
 * The workspace in the URL.
 *
 * `selection` was React state and nothing else, so every reload — and every link
 * anyone pasted to a colleague — landed on the first category of the first
 * domain (Restaurant → Identity) no matter where the person had been. The hash
 * carries it now: `#m/printers` for a module, `#c/billing/charges` for a
 * registry category.
 *
 * Slugs are lossy ("Paper & Defaults" → "paper-defaults"), so reading is a match
 * against the live tree rather than an attempt to invert them. An unknown hash
 * resolves to null and the caller falls back to the default landing.
 */
const hashFor = selection => {
  if (selection?.kind === "module") return `#m/${selection.id}`;
  if (selection?.kind === "category") return `#c/${slug(selection.domain)}/${slug(selection.category)}`;
  return "";
};

const selectionFromHash = (hash, tree, modules) => {
  const parts = (hash || "").replace(/^#/, "").split("/").filter(Boolean);
  if (parts[0] === "m" && parts[1]) {
    const found = modules.find(m => m.id === parts[1]);
    return found ? { kind: "module", id: found.id } : null;
  }
  if (parts[0] === "c" && parts[1] && parts[2]) {
    for (const domain of tree) {
      if (slug(domain.domain) !== parts[1]) continue;
      for (const category of domain.categories) {
        if (slug(category.category) === parts[2]) {
          return { kind: "category", domain: domain.domain, category: category.category };
        }
      }
    }
  }
  return null;
};

export default function AdminPage() {
  const { token, user, permissions, settings, setSettings } = usePOS();
  const adminData = useAdminData(token);

  // The server is the real gate: PUT /api/settings/{key} answers 403 without
  // settings.group.<group>.edit. Reading the effective set /auth/login already
  // computed only stops the screen from offering a write the server will refuse.
  // It must be the permission and not the role: a captain holds the edit
  // permission for no group, but a CASHIER holds it for general, billing, kot and
  // printing by default, so `role === "admin"` would lock out a role the server
  // lets through — and per-user overrides can move either way. `permissions` is
  // null for a session stored before login started keeping it; the role default
  // is the fallback then, same as canReprintBill.
  const canEditSettings = Array.isArray(permissions)
    ? permissions.some(name => /^settings\.group\..+\.edit$/.test(name))
    : user?.role !== "captain";
  // Same rule for the menu: the server gates every menu write on `menu.edit`.
  const canEditMenu = Array.isArray(permissions)
    ? permissions.includes("menu.edit")
    : user?.role !== "captain";

  const [metadata, setMetadata] = useState([]);
  const [resolvedContext, setResolvedContext] = useState(null);
  const [loading, setLoading] = useState(true);

  // The one editing context this page has (§18): the outlet layer. Not state —
  // there is no control that changes it.
  const scope = OUTLET_SCOPE;
  const [selection, setSelection] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  // Bands the admin closed on a page that has no everyday settings (those open by default).
  const [closedBands, setClosedBands] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [focusKey, setFocusKey] = useState("");

  const authHeader = useMemo(
    () => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} }),
    [token]
  );

  const loadSettings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data: registry } = await axios.get(`${API}/settings/metadata`, authHeader);
      setMetadata(registry);

      const { data } = await axios.get(`${API}/settings/resolved`, authHeader);
      setResolvedContext(data);
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setLoading(false);
    }
  }, [token, authHeader]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  /**
   * The one write path: Save Changes commits every staged key at once through
   * PUT /api/settings/bulk, which validates the prospective configuration
   * atomically — a bad combination 400s with the reason and NOTHING lands.
   * Per-field saves are gone deliberately: they let each keystroke mutate live
   * outlet configuration and made cross-setting mistakes untraceable.
   */
  const saveBatch = async changes => {
    try {
      await axios.put(`${API}/settings/bulk`, {
        changes: Object.fromEntries(changes),
        // The editing context is the outlet layer, but the bulk endpoint validates
        // against the registry OWNER (OwnerEntity.OUTLET) — the read side calls the
        // same layer "GLOBAL", so the write translates explicitly.
        context: { scope_type: "OUTLET", scope_id: null },
      }, authHeader);
      toast.success(changes.length === 1 ? "Setting saved" : `${changes.length} settings saved`);
      await loadSettings();
    } catch (error) {
      toast.error(describeError(error));
      throw error;
    }
  };

  const tree = useMemo(() => buildConfigTree(metadata), [metadata]);

  // Land somewhere real the moment the registry arrives. A console that opens on
  // an empty workspace reads as broken (§44), so the first domain's first
  // category is the landing page and its path in the tree is already open.
  useEffect(() => {
    if (selection || !tree.length) return;
    // The hash wins when it names something that still exists; otherwise the
    // first category of the first domain, as before.
    const fromUrl = selectionFromHash(window.location.hash, tree, ADMIN_MODULES);
    const initial = fromUrl || defaultSelection(tree);
    if (!initial) return;
    setSelection(initial);
    if (initial.kind === "category") {
      setExpanded(new Set(pathToCategory(initial.domain, initial.category)));
    }
  }, [tree, selection]);

  // Mirror the workspace into the URL. replaceState, not a hash assignment: the
  // owner clicking through eight categories should not have to press Back eight
  // times to leave the page.
  useEffect(() => {
    const next = hashFor(selection);
    // A module may own a sub-path (`#m/menu/categories` is the Menu hub's tab); keep it.
    if (!next || next === window.location.hash || window.location.hash.startsWith(`${next}/`)) return;
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${next}`);
  }, [selection]);

  const toggleNode = useCallback(nodeId => {
    setExpanded(previous => {
      const next = new Set(previous);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  }, []);

  /**
   * Open a category page, optionally landing on one setting inside it.
   *
   * The band the key sits in is opened too. That replaces the old "raise the
   * complexity tier before jumping" dance: an advanced row is no longer absent
   * from the DOM, it is behind a disclosure, and navigation opens the disclosure.
   */
  const openCategory = useCallback((domain, category, key = "") => {
    setSelection({ kind: "category", domain, category });
    setFocusKey(key);
    setQuery("");
    const band = key ? locateSetting(tree, key)?.band : null;
    setExpanded(previous => new Set([...previous, ...pathToCategory(domain, category, band)]));
  }, [tree]);

  // A twisty that only ever opens is not a twisty. Clicking the category you are
  // already on folds its settings away; clicking any other one navigates.
  const onCategoryClick = useCallback((domain, category) => {
    const nodeId = categoryNodeId(domain, category);
    const here = selection?.kind === "category" && selection.domain === domain && selection.category === category;
    if (here && expanded.has(nodeId)) { toggleNode(nodeId); return; }
    openCategory(domain, category);
  }, [selection, expanded, toggleNode, openCategory]);

  const openDomain = useCallback(domain => {
    const nodeId = domainNodeId(domain);
    if (expanded.has(nodeId)) { toggleNode(nodeId); return; }
    const category = firstCategory(tree, domain);
    if (category) openCategory(domain, category);
    else setExpanded(previous => new Set([...previous, nodeId]));
  }, [expanded, toggleNode, tree, openCategory]);

  const openModule = useCallback(id => {
    setSelection({ kind: "module", id });
    setFocusKey("");
    setQuery("");
  }, []);

  const onPick = useCallback(hit => {
    if (hit.kind !== "setting") { openModule(hit.sectionId); return; }
    const located = locateSetting(tree, hit.key);
    // The tree is the only thing that renders settings, so navigate to where the
    // row actually is rather than to where the registry says it should be.
    if (located) openCategory(located.domain, located.category, hit.key);
    else if (hit.sectionId && hit.categoryId) openCategory(hit.sectionId, hit.categoryId, hit.key);
  }, [tree, openCategory, openModule]);

  const clearFocus = useCallback(key => {
    setFocusKey(current => (current === key ? "" : current));
  }, []);

  const module = selection?.kind === "module"
    ? ADMIN_MODULES.find(entry => entry.id === selection.id)
    : null;

  const category = useMemo(() => {
    if (selection?.kind !== "category") return null;
    const domain = tree.find(entry => entry.domain === selection.domain);
    return domain?.categories.find(entry => entry.category === selection.category) || null;
  }, [tree, selection]);

  // The scope changes a full render before the payload it asked for arrives, so
  // pairing the new scope with the settings still in hand would put a sentence on
  // screen that is not true of either — "Override active on Tandoor" under a value
  // that came from the outlet. The resolver states which scope it answered for
  // (config_resolver returns context.scope), so the rows wait for the two to agree.
  const resolvedScope = resolvedContext?.context?.scope;
  const resolving = loading || (!!resolvedScope && resolvedScope !== scope.type);

  // Staged edits over the resolved set. The drafts hook is mounted once here —
  // NOT conditionally — so navigating between categories can never reset it and
  // unsaved work survives every navigation (the "state stays stable" contract).
  const draftsApi = useSettingsDrafts(resolvedContext?.settings);
  const { dirtyKeys, isDirty, saveState, revert, commit, valueOf } = draftsApi;

  const metadataByKey = useMemo(() => {
    const map = {};
    for (const meta of metadata) map[meta.key] = meta;
    return map;
  }, [metadata]);

  // Client-side mirror of the server's cross-setting validator: an invalid
  // combination blocks Save with the exact fix before any request is sent.
  const stagedChanges = useMemo(() => {
    const changes = {};
    for (const key of dirtyKeys) changes[key] = valueOf(key);
    return changes;
  }, [dirtyKeys, valueOf]);
  const validationProblems = useMemo(
    () => validateDraftBatch(stagedChanges, resolvedContext?.settings, metadataByKey),
    [stagedChanges, resolvedContext, metadataByKey]
  );

  // Financial / restart-required keys get a confirmation dialog on save.
  const dangerKeys = useMemo(
    () => dirtyKeys.filter(key =>
      metadataByKey[key]?.config_class === "financial"
      || metadataByKey[key]?.requires_restart
      || metadataByKey[key]?.requires_reload
    ),
    [dirtyKeys, metadataByKey]
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onSaveClick = needsConfirm => {
    if (needsConfirm) setConfirmOpen(true);
    else void performSave();
  };

  const performSave = async () => {
    setConfirmOpen(false);
    try {
      await commit(saveBatch);
    } catch {
      /* state already carries "failed"; the bar says what happened */
    }
  };

  const sectionId = module ? module.id : selection?.domain || "none";
  const crumbs = module
    ? ["Administration", "Management", module.title]
    : selection
      ? ["Administration", selection.domain, selection.category]
      : ["Administration", "Settings"];

  return (
    <div className="flex h-full overflow-hidden bg-background" data-testid="admin-control-centre" data-density="compact">
      <AdminNavTree
        tree={tree}
        modules={ADMIN_MODULES}
        selection={selection}
        expanded={expanded}
        onToggle={toggleNode}
        onOpenDomain={openDomain}
        onOpenCategory={onCategoryClick}
        onOpenSetting={openCategory}
        onOpenModule={openModule}
        focusKey={focusKey}
      />

      <section className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* DESIGN.md §4 page header: where you are and what it applies to on the left,
            search on the right — one band instead of three stacked strips. */}
        <header className="shrink-0 border-b bg-card px-loose py-snug flex items-center justify-between gap-base">
          <div className="min-w-0">
            <nav className="flex items-center gap-1 min-w-0 text-meta text-muted-foreground" aria-label="Breadcrumb" data-testid="admin-breadcrumb">
              {crumbs.map((crumb, index) => (
                <span key={`${crumb}-${index}`} className="flex items-center gap-1 min-w-0">
                  {index > 0 && <ChevronRight className="w-3 h-3 shrink-0 opacity-60" aria-hidden="true" />}
                  {index === crumbs.length - 1
                    ? <strong className="truncate text-lede font-semibold text-foreground">{crumb}</strong>
                    : <span className="truncate">{crumb}</span>}
                </span>
              ))}
            </nav>
            <div className="mt-0.5 text-meta text-muted-foreground truncate" data-testid="admin-applies-to">
              <span>Applies to: <strong className="font-medium text-foreground">This Outlet</strong></span>
              <span> · the value every terminal, station and printer inherits</span>
            </div>
          </div>
          <div className="w-48 sm:w-72 shrink-0">
            <AdminSearch query={query} onQuery={setQuery} registry={metadata} onPick={onPick} />
          </div>
        </header>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-loose" data-testid={`admin-section-${sectionId}-panel`}>
          {module
            ? <ModulePanel
                id={module.id}
                token={token}
                adminData={adminData}
                canEditMenu={canEditMenu}
                settings={settings}
                setSettings={setSettings}
              />
            : <div className="max-w-2xl space-y-2">
                {!canEditSettings && (
                  <div
                    className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-meta text-warning"
                    role="status"
                    data-testid="settings-read-only-notice"
                  >
                    <Lock className="w-3.5 h-3.5 mt-px shrink-0" />
                    <span>
                      Your role can read these settings but not change them — the local server rejects the write.
                      Ask an admin to make the change.
                    </span>
                  </div>
                )}

                {resolving && (
                  <p className="px-3 py-6 text-meta text-muted-foreground" data-testid="admin-settings-loading">
                    Resolving configuration for {scope.label}…
                  </p>
                )}

                {!loading && !tree.length && (
                  <div className="rounded-md border bg-card px-3 py-6 text-center text-meta text-muted-foreground" data-testid="admin-registry-empty">
                    <ServerCrash className="w-6 h-6 mx-auto mb-2 opacity-40" />
                    <p className="font-semibold">No configuration registry</p>
                    <p className="mt-0.5">GET /api/settings/metadata returned nothing this build can bind.</p>
                  </div>
                )}

                {!resolving && category?.bands.map(band => (
                  <SettingBand
                    key={band.nodeId}
                    band={band}
                    // A page with no everyday settings opens its band: a Charges page
                    // showing nothing but a closed "Advanced options" box read as empty
                    // (floor report 2026-10-19). The twisty still closes it.
                    open={
                      band.id === "normal" ||
                      expanded.has(band.nodeId) ||
                      (!category.bands.some(other => other.id === "normal") && !closedBands.has(band.nodeId))
                    }
                    onToggle={() => {
                      if (category.bands.some(other => other.id === "normal")) return toggleNode(band.nodeId);
                      const isOpen = expanded.has(band.nodeId) || !closedBands.has(band.nodeId);
                      setClosedBands(previous => {
                        const next = new Set(previous);
                        if (isOpen) next.add(band.nodeId); else next.delete(band.nodeId);
                        return next;
                      });
                      if (isOpen && expanded.has(band.nodeId)) toggleNode(band.nodeId);
                    }}
                    resolved={resolvedContext?.settings}
                    valueOf={draftsApi.valueOf}
                    dirtyKeys={dirtyKeys}
                    onDraft={draftsApi.setDraft}
                    readOnly={!canEditSettings}
                    focusKey={focusKey}
                    onTouched={clearFocus}
                    problems={validationProblems}
                  />
                ))}

                {!resolving && (
                  <SettingsAuditPanel
                    token={token}
                    metadataByKey={metadataByKey}
                    keys={category ? category.bands.flatMap(band => band.items.map(item => item.key)) : null}
                  />
                )}
              </div>}

          {canEditSettings && !module && (
            <SettingsSaveBar
              dirtyKeys={dirtyKeys}
              metadataByKey={metadataByKey}
              saveState={saveState}
              problems={validationProblems}
              dangerKeys={dangerKeys}
              onSave={onSaveClick}
              onDiscard={revert}
              confirmOpen={confirmOpen}
              onConfirmOpenChange={setConfirmOpen}
              onConfirmSave={performSave}
            />
          )}
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── Specialized modules ────────────────────────────────────────── */

/**
 * The parts of the admin that are not restaurant_settings rows: a printer's IP,
 * the floor plan, the menu, the staff list, the report pack, the import/export
 * and the health probes. They mount inside the same tree so there is one place to
 * look, and each is the module that already owns its own server calls.
 */
function ModulePanel({ id, token, adminData, canEditMenu, settings, setSettings }) {
  if (id === "menu") {
    return <MenuHub token={token} readOnly={!canEditMenu} />;
  }

  if (id === "floor") {
    // The floor hub (2026-10-25): designer · tables · areas, like the menu hub.
    return <FloorHub />;
  }

  // The printer registry (name, IP, port, copies, paper width, font) and the
  // routing panel (item → printer, table → bill printer, the cashier bill-printer
  // lock) are the only way to configure printing hardware in this build. Both were
  // once imported and never rendered, which left every one of those controls
  // curl-only.
  if (id === "printers") {
    return (
      <div className="space-y-3">
        {/* One summary line (collapsed) for print problems, then the setup itself. */}
        <PrintJobsPanel />
        <PrinterRegistry />
        <div data-testid="print-routing-mount"><PrintRoutingPanel /></div>
      </div>
    );
  }

  if (id === "staff") {
    return (
      <div className="space-y-3">
        <StaffPanel staff={adminData.staff} onSave={adminData.saveStaff} onDeactivate={adminData.deactivateStaff} onRemove={adminData.removeStaff} />
        <RolesPanel catalog={adminData.catalog} rolePerms={adminData.rolePerms} onKindsChanged={adminData.reload} />
      </div>
    );
  }

  if (id === "reports") {
    return (
      <div className="space-y-3">
        <CustomReportBuilder />
        <ReportsPanel />
      </div>
    );
  }

  if (id === "data") return <div className="space-y-6"><SettingsPortability /><BackupsPanel /></div>;

  if (id === "lists") return <OrderListsPanel />;

  if (id === "logs") return <DeviceLogsPanel />;

  if (id === "integrations") return <div className="space-y-4"><RemoteAccessPanel /><TelegramHub /><AiAssistantPanel /></div>;

  if (id === "system") {
    return (
      <div className="space-y-3">
        <HealthStrip token={token} />
        <div className="rounded-md border bg-card" data-testid="terminal-preferences">
          <div className="border-b px-3 py-2">
            <h3 className="text-body font-bold">Terminal preferences</h3>
            <p className="text-micro text-muted-foreground">
              Stored on this till, not on the server. Every other terminal keeps its own.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3 px-3 py-2" data-testid="realtime-sync-setting">
            <div>
              <div className="text-body font-semibold leading-tight">Production WebSocket sync</div>
              <div className="text-micro leading-tight text-muted-foreground mt-0.5">
                Turning this off isolates this till from the live order feed — a local troubleshooting switch.
              </div>
            </div>
            <Switch
              checked={!!settings?.realtimeEnabled}
              onCheckedChange={value => setSettings(previous => ({ ...previous, realtimeEnabled: value }))}
              data-testid="realtime-sync-switch"
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/* ── AdminSearch ────────────────────────────────────────────────── */

/**
 * One input across the whole admin surface. Sixty-odd registry settings spread
 * over six domains, plus the modules, is well past the point where an owner can be
 * expected to guess which branch "the thank-you line on the bill" lives under. The
 * matching is on synonyms as well as keys and the server's own display_name, so
 * the words an owner would use find the control (see searchAdmin in
 * lib/settingsMap.js). Modules are indexed too: a printer's font size is a printers
 * column, not a settings row, but "font" still has to lead somewhere.
 *
 * A result hands back the whole hit, and the page opens the tree down to the row —
 * domain, category, disclosure band and all (§22).
 */
function AdminSearch({ query, onQuery, registry, onPick }) {
  const results = useMemo(() => searchAdmin(query, registry || []), [query, registry]);
  const shown = results.slice(0, 12);

  return <div className="admin-search !mb-0" data-testid="admin-search">
    <label className="flex items-center gap-2 rounded-md border border-input bg-card px-2 h-8 focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/15">
      <Search className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
      {/* !min-h-0 is load-bearing. App.css gives every input a 48px min-height
          for touch targets, at a specificity (0,2,1) that a plain
          `.admin-search input` rule cannot beat. Inside this 28px label the
          input overflowed 20px, centred — 10px of it sat on top of the
          breadcrumb and sliced "Administration › … › Identity" in half. */}
      <input
        type="search"
        className="flex-1 min-w-0 h-full !min-h-0 bg-transparent text-body outline-none"
        value={query}
        placeholder="Search every setting — try “footer”, “gst”, “font”, “discount”"
        onChange={event => onQuery(event.target.value)}
        aria-label="Search admin settings"
        data-testid="admin-search-input"
      />
      {query && <button type="button" className="shrink-0 text-muted-foreground" onClick={() => onQuery("")} aria-label="Clear search" data-testid="admin-search-clear"><X className="w-3.5 h-3.5"/></button>}
    </label>
    {query.trim().length >= 2 && <div className="admin-search-results" data-testid="admin-search-results">
      {!results.length && <p data-testid="admin-search-empty"><small>Nothing matches “{query}”. Settings no server code reads are deliberately absent — see EXCLUDED_SETTING_KEYS.</small></p>}
      {shown.map(hit => <button
        type="button"
        key={hit.id}
        onClick={() => onPick(hit)}
        data-testid={`admin-search-result-${hit.id}`}
      >
        <strong>{hit.label}</strong>
        <small>{hit.sectionTitle} · {hit.where}</small>
      </button>)}
      {results.length > shown.length && <p data-testid="admin-search-more"><small>{results.length - shown.length} more — keep typing.</small></p>}
    </div>}
  </div>;
}

/* ── HealthStrip ────────────────────────────────────────────────── */

/**
 * The strip used to read "Online · 8ms" — a string literal that stayed green with
 * the server unplugged — beside "Backend handoff ready" and "Portable · version 1".
 * Every tile shows what an endpoint returned, or says it could not be reached.
 */
function HealthStrip({ token }) {
  const [probes, setProbes] = useState(null);

  useEffect(() => {
    let alive = true;
    const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
    const probe = path =>
      axios.get(`${API}${path}`, auth)
        .then(({ data }) => ({ ok: true, data }))
        .catch(error => ({ ok: false, error: describeError(error) }));
    Promise.all([probe("/"), probe("/admin/health"), probe("/realtime/health")])
      .then(([capabilities, server, realtime]) => {
        if (alive) setProbes({ capabilities, server, realtime });
      });
    return () => { alive = false; };
  }, [token]);

  // The icon's tint follows what the probe said. It was always green, so a stale
  // print worker read "Heartbeat stale" beside a green printer (2026-10-27).
  // The words carry the state too; colour is the second cue (DESIGN.md §2.4 rule 4).
  const tile = (Icon, label, body, testId, tone = "pending") =>
    <div data-testid={testId} data-tone={tone}><span className="health-icon" data-tone={tone}><Icon/></span><p><small>{label}</small><strong>{body}</strong></p></div>;

  if (!probes) {
    return <div className="health-strip" data-testid="admin-health-strip">
      {tile(Wifi, "Local server", "Checking…", "health-local-server")}
      {tile(HardDrive, "Database", "Checking…", "health-database")}
      {tile(Printer, "Print worker", "Checking…", "health-print-worker")}
      {tile(Activity, "Terminals", "Checking…", "health-terminals")}
    </div>;
  }

  const { capabilities, server, realtime } = probes;
  const connections = realtime.ok ? realtime.data?.connections || {} : {};
  const connected = Object.values(connections).reduce((total, count) => total + (Number(count) || 0), 0);
  const roles = Object.entries(connections).filter(([, count]) => count > 0).map(([role, count]) => `${role} ${count}`).join(", ");

  return <div className="health-strip" data-testid="admin-health-strip">
    {tile(Wifi, "Local server",
      capabilities.ok ? `Reachable · ${(capabilities.data?.capabilities || []).join(", ") || "no capabilities advertised"}` : `Unreachable · ${capabilities.error}`,
      "health-local-server", capabilities.ok ? "ok" : "bad")}
    {tile(HardDrive, "Database",
      server.ok ? `PostgreSQL ${server.data?.db || "unknown"}` : `Unknown · ${server.error}`,
      "health-database", !server.ok ? "bad" : server.data?.db === "ok" ? "ok" : "bad")}
    {tile(Printer, "Print worker",
      server.ok ? `Heartbeat ${server.data?.print_worker || "unknown"}` : `Unknown · ${server.error}`,
      "health-print-worker", !server.ok ? "bad" : server.data?.print_worker === "ok" ? "ok" : "warn")}
    {tile(Activity, "Terminals",
      realtime.ok
        ? (connected ? `${connected} connected · ${roles}` : "No terminals connected")
        : `Realtime unavailable · ${realtime.error}`,
      "health-terminals", !realtime.ok ? "bad" : connected ? "ok" : "warn")}
  </div>;
}
